import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import GoldButton from '../components/GoldButton';
import StatusBadge from '../components/StatusBadge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { LogOut, Plus, Eye, Search, Calendar, Mail, Building2, Users, Shield, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import AdminManagement from './AdminManagement';
import ConcesionariosManagement from './ConcesionariosManagement';
import TemplatesManagement from '../components/TemplatesManagement';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [filteredBeneficiarios, setFilteredBeneficiarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState('beneficiarios');
  const [gmailConnected, setGmailConnected] = useState(false);
  const [userRole, setUserRole] = useState('admin');
  const [userName, setUserName] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCedula, setNewCedula] = useState('');
  const [adding, setAdding] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [sortBy, setSortBy] = useState('fecha');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    api.get('/auth/me')
      .then(res => {
        setUserRole(res.data.role || 'admin');
        setUserName(res.data.name || res.data.email || '');
        fetchBeneficiarios();
        checkGmailStatus();
      })
      .catch(() => navigate('/admin/login', { replace: true }));
  }, [navigate]);

  const checkGmailStatus = async () => {
    try {
      const response = await api.get('/oauth/gmail/status');
      setGmailConnected(response.data.connected);
    } catch {
      setGmailConnected(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      const response = await api.get('/oauth/gmail/login');
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch {
      toast.error('Error al conectar con Gmail');
    }
  };

  const fetchBeneficiarios = async () => {
    try {
      const response = await api.get('/admin/beneficiarios?limit=1000');
      // La respuesta ahora tiene formato: { data: [...], total, skip, limit, has_more }
      const beneficiariosData = response.data.data || response.data;
      const sorted = beneficiariosData.sort((a, b) => {
        if (!a.fecha_entrega && !b.fecha_entrega) return 0;
        if (!a.fecha_entrega) return 1;
        if (!b.fecha_entrega) return -1;
        return new Date(a.fecha_entrega) - new Date(b.fecha_entrega);
      });
      setBeneficiarios(sorted);
      setFilteredBeneficiarios(sorted);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login', { replace: true });
      } else {
        toast.error('Error al cargar beneficiarios');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = beneficiarios;
    
    // Apply search filter
    if (searchTerm) {
      filtered = beneficiarios.filter(b =>
        b.cedula?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let compareA, compareB;
      
      switch (sortBy) {
        case 'nombre':
          compareA = (a.nombre || '').toLowerCase();
          compareB = (b.nombre || '').toLowerCase();
          break;
        case 'cedula':
          compareA = a.cedula || '';
          compareB = b.cedula || '';
          break;
        case 'estado':
          compareA = a.estado || '';
          compareB = b.estado || '';
          break;
        case 'fecha':
        default:
          // Handle dates - put null dates at the end
          if (!a.fecha_entrega && !b.fecha_entrega) return 0;
          if (!a.fecha_entrega) return 1;
          if (!b.fecha_entrega) return -1;
          compareA = new Date(a.fecha_entrega);
          compareB = new Date(b.fecha_entrega);
          break;
      }
      
      if (sortBy === 'fecha') {
        return sortOrder === 'asc' 
          ? compareA - compareB 
          : compareB - compareA;
      }
      
      if (compareA < compareB) return sortOrder === 'asc' ? -1 : 1;
      if (compareA > compareB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredBeneficiarios(sorted);
  }, [searchTerm, beneficiarios, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleAddBeneficiario = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post('/admin/beneficiarios', { cedula: newCedula });
      toast.success('Beneficiario agregado');
      setShowAddModal(false);
      setNewCedula('');
      fetchBeneficiarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar beneficiario');
    } finally {
      setAdding(false);
    }
  };

  const handleSyncAllStrapi = async () => {
    setSyncingAll(true);
    const toastId = toast.loading('Sincronizando todos los beneficiarios con Strapi...');
    try {
      const response = await api.post('/admin/beneficiarios/sync-all-strapi');
      const { procesados, actualizados, errores } = response.data;
      toast.success(
        `Sincronización completada: ${actualizados}/${procesados} actualizados` +
        (errores?.length ? ` · ${errores.length} con incidencias` : ''),
        { id: toastId }
      );
      fetchBeneficiarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sincronizar con Strapi', { id: toastId });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    navigate('/admin/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-[#F5A623] text-xl font-semibold">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex">
      {/* Sidebar */}
      <div className="w-64 bg-[#2A2A2A] border-r border-white/10 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_cb23de51-57d5-4133-8f16-28731c09c0ec/artifacts/6u7zs9oj_KOMPA%204.png"
              alt="KOMPA Logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-lg text-white font-bold">La Meta</h1>
              <p className="text-xs text-[#666666]">Admin Panel</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <button
            onClick={() => setCurrentView('beneficiarios')}
            data-testid="nav-beneficiarios"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
              currentView === 'beneficiarios'
                ? 'bg-[#F5A623] text-black font-semibold'
                : 'text-white hover:bg-[#3A3A3A]'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Beneficiarios</span>
          </button>

          <button
            onClick={() => setCurrentView('templates')}
            data-testid="nav-templates"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
              currentView === 'templates'
                ? 'bg-[#F5A623] text-black font-semibold'
                : 'text-white hover:bg-[#3A3A3A]'
            }`}
          >
            <Mail className="w-5 h-5" />
            <span>Plantillas</span>
          </button>

          <button
            onClick={() => setCurrentView('concesionarios')}
            data-testid="nav-concesionarios"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-2 ${
              currentView === 'concesionarios'
                ? 'bg-[#F5A623] text-black font-semibold'
                : 'text-white hover:bg-[#3A3A3A]'
            }`}
          >
            <Building2 className="w-5 h-5" />
            <span>Concesionarios</span>
          </button>

          {userRole === 'superadmin' && (
            <button
              onClick={() => setCurrentView('administradores')}
              data-testid="nav-administradores"
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                currentView === 'administradores'
                  ? 'bg-[#EF4444] text-white font-semibold'
                  : 'text-white hover:bg-[#3A3A3A]'
              }`}
            >
              <Shield className="w-5 h-5" />
              <span>Administradores</span>
            </button>
          )}
        </nav>

        <div className="p-4 border-t border-white/10">
          {!gmailConnected ? (
            <button
              onClick={handleConnectGmail}
              className="w-full flex items-center gap-3 px-4 py-3 mb-3 bg-[#EF4444]/20 text-[#EF4444] border-2 border-[#EF4444]/30 hover:bg-[#EF4444]/30 rounded-lg transition-colors font-semibold"
              data-testid="connect-gmail-button"
            >
              <Mail className="w-5 h-5" />
              <span>Conectar Gmail</span>
            </button>
          ) : (
            <div className="mb-3 px-4 py-3 bg-[#10B981]/20 border border-[#10B981]/30 rounded-lg">
              <p className="text-[#10B981] text-sm font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Gmail Conectado
              </p>
            </div>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#666666] hover:text-[#EF4444] hover:bg-[#3A3A3A] rounded-lg transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesion</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <div className="sticky top-0 z-50 bg-[#1A1A1A]/70 backdrop-blur-xl border-b border-white/10">
          <div className="px-6 py-4">
            <h2 className="text-2xl text-white font-bold">
              {currentView === 'beneficiarios' && 'Beneficiarios'}
              {currentView === 'templates' && 'Plantillas de Correo'}
              {currentView === 'concesionarios' && 'Concesionarios'}
              {currentView === 'administradores' && 'Administradores'}
            </h2>
          </div>
        </div>

        <div className="p-6">
          {currentView === 'beneficiarios' && (
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1 max-w-md relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#666666]" />
                    <Input
                      type="text"
                      placeholder="Buscar por cedula o nombre..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 bg-[#2A2A2A] border-white/20 text-white text-sm"
                      data-testid="search-input"
                    />
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <GoldButton
                      onClick={handleSyncAllStrapi}
                      variant="ghost"
                      disabled={syncingAll}
                      testId="sync-all-strapi-button"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 inline ${syncingAll ? 'animate-spin' : ''}`} />
                      {syncingAll ? 'Sincronizando...' : 'Sincronizar Todos'}
                    </GoldButton>
                    <GoldButton
                      onClick={() => setShowAddModal(true)}
                      variant="primary"
                      testId="add-beneficiary-button"
                    >
                      <Plus className="w-4 h-4 mr-1 inline" />
                      Agregar
                    </GoldButton>
                  </div>
                </div>
              </div>

              <div className="bg-[#2A2A2A] border border-white/10 rounded-lg overflow-hidden" data-testid="beneficiaries-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A1A1A] border-b border-white/10">
                      <tr>
                        <th 
                          className="text-left px-3 py-3 text-[#F5A623] font-semibold text-xs uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors"
                          onClick={() => handleSort('nombre')}
                        >
                          <div className="flex items-center gap-1">
                            <span>Nombre</span>
                            {sortBy === 'nombre' ? (
                              sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-40" />
                            )}
                          </div>
                        </th>
                        <th 
                          className="text-left px-3 py-3 text-[#F5A623] font-semibold text-xs uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors"
                          onClick={() => handleSort('cedula')}
                        >
                          <div className="flex items-center gap-1">
                            <span>Cédula</span>
                            {sortBy === 'cedula' ? (
                              sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-40" />
                            )}
                          </div>
                        </th>
                        <th className="text-left px-3 py-3 text-[#F5A623] font-semibold text-xs uppercase tracking-wider">Email</th>
                        <th 
                          className="text-left px-3 py-3 text-[#F5A623] font-semibold text-xs uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors"
                          onClick={() => handleSort('fecha')}
                        >
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>Fecha</span>
                            {sortBy === 'fecha' ? (
                              sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-40" />
                            )}
                          </div>
                        </th>
                        <th 
                          className="text-left px-3 py-3 text-[#F5A623] font-semibold text-xs uppercase tracking-wider cursor-pointer hover:bg-white/5 transition-colors"
                          onClick={() => handleSort('estado')}
                        >
                          <div className="flex items-center gap-1">
                            <span>Estado</span>
                            {sortBy === 'estado' ? (
                              sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-40" />
                            )}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBeneficiarios.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-12 text-center text-[#666666] text-sm">
                            {searchTerm ? 'No se encontraron resultados' : 'No hay beneficiarios registrados'}
                          </td>
                        </tr>
                      ) : (
                        filteredBeneficiarios.map((beneficiario) => (
                          <motion.tr
                            key={beneficiario.cedula}
                            onClick={() => navigate(`/admin/beneficiario/${beneficiario.cedula}`)}
                            className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            data-testid={`beneficiary-row-${beneficiario.cedula}`}
                          >
                            <td className="px-3 py-3 text-white text-sm" title={beneficiario.nombre}>
                              {beneficiario.nombre || 'Sin nombre'}
                            </td>
                            <td className="px-3 py-3 text-white text-sm whitespace-nowrap">{beneficiario.cedula}</td>
                            <td className="px-3 py-3 text-[#666666] text-xs max-w-[180px] truncate" title={beneficiario.email}>
                              {beneficiario.email}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              {beneficiario.fecha_entrega ? (
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-[#10B981] flex-shrink-0" />
                                  <span className="text-[#10B981] text-xs font-semibold">
                                    {new Date(beneficiario.fecha_entrega).toLocaleDateString('es-ES', {
                                      day: '2-digit', month: 'short'
                                    })}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[#666666] italic text-xs">Sin asignar</span>
                              )}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap"><StatusBadge estado={beneficiario.estado} /></td>
                          </motion.tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {currentView === 'templates' && (
            <TemplatesManagement />
          )}

          {currentView === 'administradores' && userRole === 'superadmin' && (
            <AdminManagement />
          )}

          {currentView === 'concesionarios' && (
            <ConcesionariosManagement />
          )}
        </div>
      </div>

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-gradient-to-br from-[#2A2A2A] to-[#1A1A1A] border border-[#F5A623]/30 rounded-2xl p-8 max-w-md" data-testid="add-beneficiary-modal">
          <DialogTitle className="text-2xl font-bold text-white mb-6">Agregar Beneficiario</DialogTitle>
          <form onSubmit={handleAddBeneficiario} className="space-y-6">
            <div>
              <Label htmlFor="new-cedula" className="text-white text-sm uppercase tracking-wider mb-2 block">
                Cedula del Beneficiario
              </Label>
              <Input
                id="new-cedula"
                type="text"
                value={newCedula}
                onChange={(e) => setNewCedula(e.target.value)}
                required
                className="bg-[#2A2A2A] border border-white/20 text-white rounded-lg px-4 py-3 w-full focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20"
                placeholder="V-12345678"
                data-testid="new-cedula-input"
              />
              <p className="mt-2 text-xs text-[#666666]">Los datos se buscaran automaticamente en Strapi</p>
            </div>
            <div className="flex gap-3">
              <GoldButton type="button" variant="ghost" onClick={() => setShowAddModal(false)} className="flex-1" testId="cancel-add-button">
                Cancelar
              </GoldButton>
              <GoldButton type="submit" variant="primary" disabled={adding} className="flex-1" testId="confirm-add-button">
                {adding ? 'Agregando...' : 'Agregar'}
              </GoldButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
